import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';

import { DEFAULT_LANG, SITE_BASE_URL } from '../constants';
import {
  cleanProfileUrl,
  JsonLd,
  ORGANIZATION_ID,
  ORGANIZATION_LOGO_URL,
  ORGANIZATION_NAME,
} from '../utils/structuredData';

interface StructuredDataProps {
  lang: string;
}

interface WebsiteData {
  sanityWebsite: {
    contacts: {
      legalName?: string;
      email?: string;
      phoneNumbers?: Array<string>;
      address?: {
        pt?: string;
        en?: string;
      };
    };
    socialMedia?: {
      facebookUrl?: string;
      linkedInUrl?: string;
      twitterUrl?: string;
      instagramUrl?: string;
    };
  };
}

// Factual description of the company, distinct from the CMS SEO marketing copy.
const DESCRIPTION_BY_LANG: Record<string, string> = {
  pt: 'Sociedade gestora de fundos de investimento mobiliário, alternativo e imobiliário em Portugal, incluindo fundos elegíveis para o Golden Visa.',
  en: 'Portugal-based management company for mutual, alternative and real estate investment funds, including funds eligible for the Golden Visa.',
};

const AREA_SERVED = ['PT', 'ES', 'LU', 'BR', 'TR', 'US'];
const MEMBER_OF = ['APPII', 'APFIPP', 'APCRI', 'CCILB'];

/**
 * Organization JSON-LD for Heed Capital, rendered on each localized homepage.
 * Contacts and social profiles come from the CMS `website` singleton.
 */
function StructuredData({ lang }: StructuredDataProps): React.ReactElement {
  const { sanityWebsite } = useStaticQuery<WebsiteData>(graphql`
    {
      sanityWebsite {
        contacts {
          legalName
          email
          phoneNumbers
          address {
            ...LocaleTextFragment
          }
        }
        socialMedia {
          facebookUrl
          linkedInUrl
          twitterUrl
          instagramUrl
        }
      }
    }
  `);

  const { contacts, socialMedia } = sanityWebsite;

  // The CMS address is multi-line free text; flatten it to one line.
  const address = (contacts.address?.[lang as 'pt' | 'en'] || contacts.address?.[DEFAULT_LANG])
    ?.replace(/\s*\n\s*/g, ', ')
    .trim();

  const sameAs = [
    socialMedia?.linkedInUrl,
    socialMedia?.facebookUrl,
    socialMedia?.instagramUrl,
    socialMedia?.twitterUrl,
  ]
    .filter((url): url is string => !!url)
    .map(cleanProfileUrl);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FinancialService',
    '@id': ORGANIZATION_ID,
    name: ORGANIZATION_NAME,
    ...(contacts.legalName && { legalName: contacts.legalName }),
    url: `${SITE_BASE_URL}/`,
    logo: ORGANIZATION_LOGO_URL,
    description: DESCRIPTION_BY_LANG[lang] || DESCRIPTION_BY_LANG[DEFAULT_LANG],
    ...(address && { address }),
    ...(contacts.phoneNumbers?.[0] && { telephone: contacts.phoneNumbers[0] }),
    ...(contacts.email && { email: contacts.email }),
    areaServed: AREA_SERVED,
    memberOf: MEMBER_OF.map(name => ({ '@type': 'Organization', name })),
    ...(sameAs.length > 0 && { sameAs }),
  };

  return <JsonLd data={structuredData} />;
}

export default StructuredData;
